import { Mention } from '@design/chat/mention-composer';
import { Button } from '@design/components/button/Button';
import { Icon } from '@design/components/icon/Icon';
import { Modal } from '@design/components/modal/Modal';
import { Spinner } from '@design/components/spinner/Spinner';
import { useRows } from '@terminal/components/chat';
import { MentionAwareText } from '@terminal/components/chat/chat-input/mentions/MentionAwareText';
import { useRefinePromptQuery } from '@terminal/react-query-hooks/prompt-refinement/useRefinePromptQuery';
import { PromptRefinerTrackingArgs } from '@terminal/services/tracking/posthog/use-posthog-events/alfredEvents';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PromptAngleCard } from './PromptAngleCard';
import styles from './PromptRefinementModal.module.scss';

const CLARIFICATION_MIN_ROWS = 5;

export interface PromptRefinementModalProps {
  isOpen: boolean;
  question: string;
  mentions?: Mention[];
  tracking: PromptRefinerTrackingArgs;
  onClose: () => void;
  onSelectPrompt: (prompt: string) => void;
  onSelectPrompts: (prompts: string[]) => void;
  allowMultiSelect: boolean;
}

export const PromptRefinementModal = ({
  isOpen,
  question,
  mentions,
  tracking,
  onClose,
  onSelectPrompt,
  onSelectPrompts,
  allowMultiSelect,
}: PromptRefinementModalProps) => {
  const { t } = useTranslation('chat');
  const {
    trackPromptRefinerClarificationAdded,
    trackPromptRefinerSuggestionEdited,
  } = usePosthogEvents();
  const [clarificationAnswer, setClarificationAnswer] = useState('');
  const [clarificationContext, setClarificationContext] = useState<{
    question: string;
    answers: string[];
  }>({ question, answers: [] });
  const [selectedPromptIndices, setSelectedPromptIndices] = useState<
    Set<number>
  >(() => new Set());
  const [promptOverrides, setPromptOverrides] = useState<
    Record<number, string>
  >({});
  const clarificationInputRef = useRef<HTMLTextAreaElement>(null);
  // Each refiner-engagement event should fire at most once per modal-open
  // lifecycle. These refs reset whenever `isOpen` flips back to true.
  const hasTrackedClarificationRef = useRef(false);
  const hasTrackedSuggestionEditRef = useRef(false);
  const { rows, updateRows } = useRows({
    value: clarificationAnswer,
    textAreaRef: clarificationInputRef,
    compactMode: true,
    defaultRows: CLARIFICATION_MIN_ROWS,
  });
  const clarificationAnswers =
    clarificationContext.question === question
      ? clarificationContext.answers
      : [];
  const refinementQuestion =
    clarificationAnswers.length > 0
      ? t('chat:promptRefinement.clarificationContext', {
          question: question.trim(),
          answers: clarificationAnswers.map(answer => `- ${answer}`).join('\n'),
        })
      : question;

  const {
    data,
    isFetching,
    isError,
    refetch: handleRetry,
  } = useRefinePromptQuery({
    question: refinementQuestion,
    mentions,
    isEnabled: isOpen,
  });

  const angles = data?.angles ?? [];
  const clarification = data?.clarification ?? null;
  const showClarification = !!clarification && !isFetching && !isError;

  useEffect(() => {
    if (!isOpen) {
      setClarificationAnswer('');
      setClarificationContext({ question: '', answers: [] });
      return;
    }

    hasTrackedClarificationRef.current = false;
    hasTrackedSuggestionEditRef.current = false;
    setSelectedPromptIndices(new Set());
    setPromptOverrides({});
  }, [isOpen]);

  const focusClarificationInput = () => {
    if (!showClarification) {
      return;
    }

    const el = clarificationInputRef.current;

    if (!el) {
      return;
    }

    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  };

  useEffect(focusClarificationInput, [showClarification]);

  const hasClarificationAnswer = !!clarificationAnswer.trim();

  const handleClearClarification = () => {
    setClarificationAnswer('');
    onClose();
  };

  const handleApplyClarification = () => {
    const trimmedAnswer = clarificationAnswer.trim();

    if (!trimmedAnswer) {
      return;
    }

    if (!hasTrackedClarificationRef.current) {
      trackPromptRefinerClarificationAdded(tracking);
      hasTrackedClarificationRef.current = true;
    }

    setClarificationAnswer('');
    setClarificationContext(previousContext => ({
      question,
      answers: [
        ...(previousContext.question === question
          ? previousContext.answers
          : []),
        trimmedAnswer,
      ],
    }));
  };

  const handleSuggestionEdited = () => {
    if (hasTrackedSuggestionEditRef.current) {
      return;
    }

    trackPromptRefinerSuggestionEdited(tracking);
    hasTrackedSuggestionEditRef.current = true;
  };

  const handleSelectionChange = (index: number, isSelected: boolean) => {
    setSelectedPromptIndices(prev => {
      const next = new Set(prev);

      if (isSelected) {
        next.add(index);
      } else {
        next.delete(index);
      }

      return next;
    });
  };

  const handlePromptChange = (index: number, prompt: string) => {
    setPromptOverrides(prev => ({
      ...prev,
      [index]: prompt,
    }));
  };

  const selectedPrompts = useMemo(
    () =>
      angles
        .map((angle, index) =>
          selectedPromptIndices.has(index)
            ? (promptOverrides[index] ?? angle.prompt)
            : null
        )
        .filter((prompt): prompt is string => !!prompt?.trim()),
    [angles, promptOverrides, selectedPromptIndices]
  );

  const hasSelectedPrompts = selectedPrompts.length > 0;

  const handleSendSelectedPrompts = () => {
    if (!hasSelectedPrompts) {
      return;
    }

    onSelectPrompts(selectedPrompts);
  };

  const modalTitle = showClarification ? (
    <span className={styles.clarificationTitle}>
      <Icon
        iconName="ExclamationCircle"
        fillColor="var(--icon-primary)"
        size={20}
      />
      {t('chat:promptRefinement.clarificationTitle')}
    </span>
  ) : (
    t('chat:promptRefinement.title')
  );
  const modalDescription = showClarification ? (
    <MentionAwareText text={clarification ?? ''} />
  ) : (
    t('chat:promptRefinement.description')
  );

  return (
    <Modal
      isOpen={isOpen}
      title={modalTitle}
      description={modalDescription}
      onClose={onClose}
      data-testid="prompt-refinement-modal"
      disableEnterClose
      primaryBtnProps={
        allowMultiSelect && !isFetching && !isError
          ? {
              label: t('chat:promptRefinement.sendSelected'),
              onClick: handleSendSelectedPrompts,
              disabled: !hasSelectedPrompts,
              'data-testid': 'prompt-refinement-send-selected-button',
            }
          : undefined
      }
      secondaryBtnProps={
        isError
          ? {
              type: 'button',
              label: t('chat:promptRefinement.retry'),
              onClick: () => handleRetry(),
            }
          : undefined
      }
    >
      <div className={styles.content}>
        {isFetching && (
          <div className={styles.statusMessage}>
            <Spinner size="small" useAccentColor />
            <p>{t('chat:promptRefinement.loading')}</p>
          </div>
        )}

        {isError && (
          <div className={styles.statusMessage} role="alert">
            <p>{t('chat:promptRefinement.error')}</p>
          </div>
        )}

        {showClarification && (
          <div className={styles.clarification}>
            <textarea
              ref={clarificationInputRef}
              className={styles.editInput}
              value={clarificationAnswer}
              onChange={e => {
                updateRows(e);
                setClarificationAnswer(e.target.value);
              }}
              rows={rows}
              aria-label={t('chat:promptRefinement.clarificationTitle')}
              data-testid="prompt-refinement-clarification-input"
            />
            <div className={styles.editActions}>
              <Button
                type="button"
                variant="tertiary"
                size="small"
                label={t('chat:promptRefinement.cancel')}
                onClick={handleClearClarification}
              />
              <Button
                type="button"
                variant="secondary"
                size="small"
                label={t('chat:promptRefinement.update')}
                onClick={handleApplyClarification}
                disabled={!hasClarificationAnswer}
                data-testid="prompt-refinement-update-button"
              />
            </div>
          </div>
        )}

        {!isFetching &&
          !isError &&
          angles.map((angle, index) => (
            <PromptAngleCard
              key={`${angle.lens}-${angle.label}`}
              angle={angle}
              isOpen={isOpen}
              onSelectPrompt={onSelectPrompt}
              onSuggestionEdited={handleSuggestionEdited}
              isSelected={selectedPromptIndices.has(index)}
              onSelectionChange={isSelected =>
                handleSelectionChange(index, isSelected)
              }
              onPromptChange={prompt => handlePromptChange(index, prompt)}
              showSelection={allowMultiSelect}
            />
          ))}
      </div>
    </Modal>
  );
};
